/**
 * The virtualized "Decyzje klienta" review DataTable, plus its no-results
 * state. Row virtualization uses useTableVirtualizer, matching every other
 * catalog-scale table in this codebase (see apps/web/AGENTS.md's
 * pagination/virtualization rule) — required to stay usable at 100+ rows.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@ooh/ui/components/data-table';
import { useTableVirtualizer } from '@ooh/ui/components/data-table-virtualizer';
import { Button } from '@ooh/ui/components/button';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import type { UnitDecisionsListItem } from '@ooh/contracts';
import type { SortDir, SortKey } from './filters';
import { getDecisionRowClassName } from './table-columns';

// Measured real row height for this table's cell layout (see the mockup's
// table.mockup.tsx for the full rationale on why this must match the DOM
// height exactly to avoid virtualizer drift).
const ROW_ESTIMATE_PX = 53.75;

// Explicit bounded viewport height, applied via inline style on the wrapper
// below — required so the virtualizer's scroll container has a real height
// to grow into. Sized for this embedded (inside a version card) context —
// shorter than a dedicated full-height screen would use, but still tall
// enough to show several rows before scrolling. No Tailwind scale step
// corresponds to this value, so it's named here instead of left as a bare
// arbitrary `h-[420px]` class.
const TABLE_VIEWPORT_HEIGHT_PX = 420;

export interface UnitDecisionsTableProps {
  rows: UnitDecisionsListItem[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: string) => void;
  onClearFilters: () => void;
  columns: ColumnDef<UnitDecisionsListItem>[];
  onColumnReorder: (fromId: string, toId: string) => void;
  onColumnResizeEnd: (columnId: string, widthPx: number) => void;
}

export function UnitDecisionsTable({
  rows,
  sortKey,
  sortDir,
  onSort,
  onClearFilters,
  columns,
  onColumnReorder,
  onColumnResizeEnd,
}: UnitDecisionsTableProps) {
  const { t } = useTranslation('offers');
  const noopFetchNextPage = useCallback(() => Promise.resolve(), []);
  const { scrollContainerRef, sentinelRef, virtualItems, totalSize } = useTableVirtualizer({
    rowCount: rows.length,
    estimateRowHeightPx: ROW_ESTIMATE_PX,
    hasNextPage: false,
    isFetchingNextPage: false,
    fetchNextPage: noopFetchNextPage,
  });

  if (rows.length === 0) {
    return (
      <div
        className="flex flex-col items-center gap-2 px-6 py-14 text-center"
        data-testid="unit-decisions-empty-filtered"
      >
        <span className="text-sm font-semibold text-foreground">
          {t('workspace.versions.unitDecisions.table.emptyTitle')}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('workspace.versions.unitDecisions.table.emptyDescription')}
        </span>
        <Button
          variant="outline"
          size="xs"
          className="mt-1"
          onClick={onClearFilters}
          data-testid="unit-decisions-clear-filters-btn"
        >
          {t('workspace.versions.unitDecisions.table.clearFiltersBtn')}
        </Button>
      </div>
    );
  }

  return (
    <div style={{ height: TABLE_VIEWPORT_HEIGHT_PX }}>
      <DataTable
        columns={columns}
        data={rows}
        getRowKey={(unit) => String(unit.mediaUnitId)}
        getRowTestId={(unit) => `unit-decision-row-${unit.mediaUnitId}`}
        getRowClassName={getDecisionRowClassName}
        sortBy={sortKey}
        sortDir={sortDir}
        onSort={onSort}
        onColumnReorder={onColumnReorder}
        onColumnResizeEnd={onColumnResizeEnd}
        virtualizer={{ scrollContainerRef, sentinelRef, virtualItems, totalSize }}
        className="h-full rounded-xl"
      />
    </div>
  );
}
